import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ActionIcon,
  Button,
  Checkbox,
  Divider,
  NumberInput,
  Select
} from '@mantine/core';
import { Edit, Plus, Template, Trash } from 'tabler-icons-react';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppFlexbox from '../../common/AppFlexbox';
import {
  ECOM_IMAGE_POSITION_ENUM,
  ECOM_IMAGE_POSITION_LIST
} from '../../../config/constants';
import AppCard from '../../common/AppCard';
import CustomerFileInput from '../../common/CustomerFileInput';
import AppImage from '../../common/AppImage';
import { fileToBase64String } from '../../../helpers/awsHelper';

const initialTemplateState = {
  sizeOption: '',
  svgSource: null,
  imageHeightPx: null,
  imageWidthPx: null,
  isDefault: false,
  fkEcomImagePosition: ECOM_IMAGE_POSITION_ENUM.FRONT,
  type: 'upload-svg'
};

const AssignPrintTemplateModal = ({
  isOpen,
  onClose,
  productDesigns,
  onConfirm,
  sizeOptions
}) => {
  const [formState, setFormState] = useState({
    showAddTemplate: false,
    templateState: { ...initialTemplateState },
    productDesigns: [],
    loading: false
  });
  const previewTemplate = formState.productDesigns.find(
    (t) => t.isPreviewTemplate
  );

  useEffect(() => {
    if (isOpen) {
      setFormState({
        showAddTemplate: false,
        templateState: { ...initialTemplateState },
        productDesigns:
          productDesigns.map((t) => ({
            ...t,
            key: t.pkEcomExternalProductPrintTemplate,
            src: t.svgSource
          })) || [],
        loading: false
      });
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      formSectionProps={
        formState.showAddTemplate
          ? {
              onCancel: () =>
                setFormState({
                  ...formState,
                  showAddTemplate: false
                }),
              isLoading: formState.loading,
              onSubmit: () => {
                setFormState({
                  ...formState,
                  productDesigns: formState.templateState.key
                    ? [
                        ...formState.productDesigns.filter(
                          (t) => t.key !== formState.templateState.key
                        ),
                        formState.templateState
                      ]
                    : [
                        ...formState.productDesigns,
                        {
                          ...formState.templateState,
                          key: new Date().getTime().toString()
                        }
                      ],
                  showAddTemplate: false
                });
              },
              cancelTitle: 'Back',
              submitTitle: formState.templateState?.key
                ? 'Save template'
                : 'Add template'
            }
          : {
              onCancel: onClose,
              isLoading: formState.loading,
              onSubmit: () => {
                onConfirm(formState.productDesigns);
              },
              cancelTitle: 'Close',
              submitTitle: 'Confirm'
            }
      }
      isOpen={isOpen}
      onClose={onClose}
      title={
        formState.showAddTemplate
          ? 'Add print templates'
          : 'Manage print templates'
      }
    >
      <AppStack style={{ gap: 16 }}>
        {formState.showAddTemplate ? (
          <AppStack style={{ gap: 20 }}>
            {!formState.templateState.isPreviewTemplate && (
              <Select
                clearable
                data={sizeOptions}
                label="Size Option"
                onChange={(v) => {
                  setFormState({
                    ...formState,
                    templateState: {
                      ...formState.templateState,
                      sizeOption: v
                    }
                  });
                }}
                placeholder="Select size option"
                required
                searchable
                value={formState.templateState.sizeOption}
              />
            )}

            <Select
              clearable
              data={ECOM_IMAGE_POSITION_LIST}
              label="Image position"
              onChange={(v) => {
                setFormState({
                  ...formState,
                  templateState: {
                    ...formState.templateState,
                    fkEcomImagePosition: v
                  }
                });
              }}
              placeholder="Select image position"
              required
              searchable
              value={formState.templateState.fkEcomImagePosition}
            />

            {!formState.templateState.isPreviewTemplate && (
              <CustomerFileInput
                file={formState.templateState.file}
                label="SVG file"
                onChange={(file) => {
                  if (file) {
                    fileToBase64String(file, (v) => {
                      setFormState((c) => ({
                        ...c,
                        templateState: {
                          ...c.templateState,
                          file,
                          imagePreview: v || file?.src || null,
                          svgSource: null,
                          src: null
                        }
                      }));
                    });
                  }
                  else {
                    setFormState((c) => ({
                      ...c,
                      templateState: {
                        ...c.templateState,
                        file: null,
                        imagePreview: null,
                        svgSource: null,
                        src: null
                      }
                    }));
                  }
                }}
                required
                value={formState.templateState.svgSource}
              />
            )}

            <AppFlexbox style={{ gap: 5, flex: 1 }}>
              <NumberInput
                allowNegative={false}
                decimalScale={0}
                disabled={formState.loading}
                fixedDecimalScale
                label="Width (px)"
                min={0}
                onChange={(v) => {
                  setFormState({
                    ...formState,
                    templateState: {
                      ...formState.templateState,
                      imageWidthPx: v
                    }
                  });
                }}
                required
                style={{ flex: 1 }}
                suffix="px"
                thousandSeparator
                value={formState.templateState.imageWidthPx ?? ''}
              />

              <NumberInput
                allowNegative={false}
                decimalScale={0}
                disabled={formState.loading}
                fixedDecimalScale
                label="Height (px)"
                min={0}
                onChange={(v) => {
                  setFormState({
                    ...formState,
                    templateState: {
                      ...formState.templateState,
                      imageHeightPx: v
                    }
                  });
                }}
                required
                style={{ flex: 1 }}
                suffix="px"
                thousandSeparator
                value={formState.templateState.imageHeightPx ?? ''}
              />
            </AppFlexbox>

            {!formState.templateState.isPreviewTemplate && (
              <Checkbox
                checked={formState.templateState.isDefault || false}
                description="This template will be used when designing products."
                label="Set as design template"
                onChange={() => {
                  setFormState({
                    ...formState,
                    templateState: {
                      ...formState.templateState,
                      isDefault: !formState.templateState.isDefault
                    }
                  });
                }}
              />
            )}

            <Checkbox
              checked={formState.templateState.isPreviewTemplate || false}
              description="This template will be used when generating the preview images. If no preview template is set, the design template will be used. Preview template is not to be used for actual printing."
              label="Set as preview template"
              onChange={() => {
                setFormState({
                  ...formState,
                  templateState: {
                    ...formState.templateState,
                    isPreviewTemplate: !formState.templateState
                      .isPreviewTemplate
                  }
                });
              }}
            />
          </AppStack>
        ) : (
          <AppStack style={{ gap: 20 }}>
            {!!previewTemplate && (
              <>
                <AppStack style={{ gap: 5 }}>
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    Printify template
                  </AppText>
                  <AppText style={{ fontSize: 14, color: '#666' }}>
                    This template will be used when generating the preview
                    images and is not to be used for actual printing.
                  </AppText>
                  <AppCard
                    radius={8}
                    showow="none"
                    style={{ padding: 0 }}
                    withBorder
                  >
                    <AppFlexbox
                      style={{
                        padding: 8,
                        gap: 8,
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                        <AppStack
                          style={{
                            width: 50,
                            height: 50,
                            overflow: 'hidden',
                            border: 'solid 1px lightgrey',
                            borderRadius: 4,
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          <Template
                            style={{
                              fontSize: 32,
                              color: '#999'
                            }}
                          />
                        </AppStack>
                        <AppStack style={{ gap: 0 }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: 500
                            }}
                          >
                            {ECOM_IMAGE_POSITION_LIST.find(
                              (pos) =>
                                pos.value.toString() ===
                                previewTemplate.fkEcomImagePosition.toString()
                            )?.label || 'Unknown Position'}
                          </AppText>
                          <AppText
                            style={{
                              fontSize: 14,
                              color: '#666'
                            }}
                          >
                            {previewTemplate.imageWidthPx}x
                            {previewTemplate.imageHeightPx}px
                          </AppText>
                          <AppText
                            style={{
                              fontSize: 14,
                              color: '#666'
                            }}
                          >
                            preview
                          </AppText>
                        </AppStack>
                      </AppFlexbox>

                      <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                        <ActionIcon
                          color="dark"
                          onClick={() => {
                            setFormState({
                              ...formState,
                              showAddTemplate: true,
                              templateState: {
                                ...previewTemplate,
                                imagePreview:
                                  previewTemplate.svgSource ||
                                  previewTemplate.imagePreview,
                                fkEcomImagePosition: previewTemplate.fkEcomImagePosition?.toString()
                              }
                            });
                          }}
                          variant="subtle"
                        >
                          <Edit size={18} />
                        </ActionIcon>
                        <ActionIcon
                          color="red"
                          onClick={() => {
                            setFormState({
                              ...formState,
                              productDesigns: formState.productDesigns.filter(
                                (pt) => pt.key !== previewTemplate.key
                              )
                            });
                          }}
                          variant="subtle"
                        >
                          <Trash size={18} />
                        </ActionIcon>
                      </AppFlexbox>
                    </AppFlexbox>
                  </AppCard>
                </AppStack>
                <Divider />
              </>
            )}
            <AppStack style={{ gap: 5 }}>
              <AppFlexbox
                style={{
                  gap: 5,
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  Assigned templates
                </AppText>

                <Button
                  color="blue"
                  leftSection={<Plus size={18} />}
                  onClick={() => {
                    setFormState({
                      ...formState,
                      showAddTemplate: true,
                      templateState: { ...initialTemplateState }
                    });
                  }}
                  size="compact-sm"
                  variant="outline"
                >
                  Add
                </Button>
              </AppFlexbox>
              {formState.productDesigns.length === 0 ? (
                <AppStack
                  style={{
                    padding: 20,
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'solid 1px lightgrey'
                  }}
                >
                  <AppText
                    style={{ fontSize: 14, color: '#666', textAlign: 'center' }}
                  >
                    No print templates assigned.
                  </AppText>
                </AppStack>
              ) : (
                <AppStack style={{ gap: 20 }}>
                  {sizeOptions.map((s) => {
                    const templates = formState.productDesigns.filter(
                      (t) => t.sizeOption === s.value && !t.isPreviewTemplate
                    );

                    return (
                      <AppCard
                        key={s.value}
                        radius={8}
                        showow="none"
                        style={{ padding: 0 }}
                        withBorder
                      >
                        <AppStack style={{ gap: 0 }}>
                          <AppStack style={{ padding: 8 }}>
                            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                              {s.label}
                            </AppText>
                          </AppStack>
                          <Divider />
                          <AppStack style={{ gap: 0 }}>
                            {templates.length === 0 ? (
                              <AppFlexbox
                                style={{
                                  padding: 8,
                                  gap: 8,
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}
                              >
                                <AppText
                                  style={{ fontSize: 14, color: '#666' }}
                                >
                                  No templates assigned for this size.
                                </AppText>
                              </AppFlexbox>
                            ) : (
                              templates.map((t, index) => (
                                <React.Fragment
                                  key={
                                    t.pkEcomExternalProductPrintTemplate ||
                                    t.key
                                  }
                                >
                                  {index > 0 && <Divider />}
                                  <AppFlexbox
                                    style={{
                                      padding: 8,
                                      gap: 8,
                                      alignItems: 'center',
                                      justifyContent: 'space-between'
                                    }}
                                  >
                                    <AppFlexbox
                                      style={{ gap: 8, alignItems: 'center' }}
                                    >
                                      <AppStack
                                        style={{
                                          width: 50,
                                          height: 50,
                                          overflow: 'hidden',
                                          border: 'solid 1px lightgrey',
                                          borderRadius: 4,
                                          justifyContent: 'center',
                                          alignItems: 'center'
                                        }}
                                      >
                                        {t.imagePreview || t.svgSource ? (
                                          <AppImage
                                            height="100%"
                                            src={t.imagePreview || t.svgSource}
                                            width="100%"
                                          />
                                        ) : (
                                          <Template
                                            style={{
                                              fontSize: 32,
                                              color: '#999'
                                            }}
                                          />
                                        )}
                                      </AppStack>
                                      <AppStack style={{ gap: 0 }}>
                                        <AppText
                                          style={{
                                            fontSize: 14,
                                            fontWeight: 500
                                          }}
                                        >
                                          {ECOM_IMAGE_POSITION_LIST.find(
                                            (pos) =>
                                              pos.value.toString() ===
                                              t.fkEcomImagePosition.toString()
                                          )?.label || 'Unknown Position'}
                                        </AppText>
                                        <AppText
                                          style={{
                                            fontSize: 14,
                                            color: '#666'
                                          }}
                                        >
                                          {t.imageWidthPx}x{t.imageHeightPx}px
                                        </AppText>

                                        {t.isDefault && (
                                          <AppText
                                            style={{
                                              fontSize: 14,
                                              color: '#666'
                                            }}
                                          >
                                            Default
                                          </AppText>
                                        )}
                                      </AppStack>
                                    </AppFlexbox>

                                    <AppFlexbox
                                      style={{ gap: 8, alignItems: 'center' }}
                                    >
                                      <ActionIcon
                                        color="dark"
                                        onClick={() => {
                                          setFormState({
                                            ...formState,
                                            showAddTemplate: true,
                                            templateState: {
                                              ...t,
                                              imagePreview:
                                                t.svgSource || t.imagePreview,
                                              fkEcomImagePosition: t.fkEcomImagePosition?.toString()
                                            }
                                          });
                                        }}
                                        variant="subtle"
                                      >
                                        <Edit size={18} />
                                      </ActionIcon>
                                      <ActionIcon
                                        color="red"
                                        onClick={() => {
                                          setFormState({
                                            ...formState,
                                            productDesigns: formState.productDesigns.filter(
                                              (pt) => pt.key !== t.key
                                            )
                                          });
                                        }}
                                        variant="subtle"
                                      >
                                        <Trash size={18} />
                                      </ActionIcon>
                                    </AppFlexbox>
                                  </AppFlexbox>
                                </React.Fragment>
                              ))
                            )}
                          </AppStack>
                        </AppStack>
                      </AppCard>
                    );
                  })}
                </AppStack>
              )}
            </AppStack>
          </AppStack>
        )}
      </AppStack>
    </ResponsiveModal>
  );
};

AssignPrintTemplateModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  productDesigns: PropTypes.array,
  sizeOptions: PropTypes.array
};

export default AssignPrintTemplateModal;
